import { useEffect, useRef, useState } from "react";
import { useClient } from "urql";
import {
  bttStart,
  bttEnd,
  QUICK_VIEW_BTT_MODAL_NAME,
} from "@lululemon/ecom-common/src/util/blue-triangle";
import Util from "@lululemon/ecom-common/src/lib/util";
import { shipAddToBag } from "@lululemon/ecom-pdp/src/graphql/addToBag";
import fetchInventoryDetails from "@lululemon/ecom-pdp/src/graphql/fetchInventoryDetails";
import Cookies from "js-cookie";
import fetchProductDetails from "./fetchProduct";
import { findSku, convertToDigitalDataCart } from "./adapter-utils";
import { Logger } from "@lululemon/mwa-logger";
import { useRouter } from "next/router";

const logger = new Logger({
  owner: "outfitting",
  app: "outfitting-app",
});

export default logger;

const util = new Util("ecom-cdp/quick-view-modal.js");
const $window = util.windowEvent();

/**
 * This hook executes the productDetailsQuery and inventoryDetailsQuery
 * to provide the data to the quickViewModal
 * @param {*} param0
 * @returns
 */
export const useFetchProductData = ({
  productCategoryUnifiedId,
  parentCategoryUnifiedId,
  productId,
  productUnifiedId,
}) => {
  const { locale } = useRouter();
  const graphqlClient = useClient();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);
  const fetched = useRef(false);
  const requestData = async () => {
    try {
      setError(null);
      const forcePcm = Cookies.get("lll-pcm-gate-qvm") == 1 || false;
      const category = productCategoryUnifiedId || parentCategoryUnifiedId;
      const queryParams = {
        locale: locale.toLowerCase().replace("_", "-"),
        productId,
        forcePcm,
        unifiedId: productUnifiedId || "",
        category: category || "",
      };
      const productResponse = await fetchProductDetails(
        queryParams,
        graphqlClient
      );
      const inventoryQueryParams = {
        productId,
        childSkus: productResponse?.productDetailPage?.skus,
      };
      const inventory = await fetchInventoryDetails(
        inventoryQueryParams,
        graphqlClient
      );
      setData({
        product: productResponse,
        inventory,
      });
    } catch (e) {
      setError(e);
      logger.info(new Error("QuickViewModal response error"), {
        fingerprint: [
          "Outfitting",
          "QuickViewModal:adapter",
          "adapter-hooks",
          "Api error",
        ],
        tags: {
          componentName: "qvm:adapter-hooks",
          functionName: "requestData",
          featureName: "Outfitting:QuickViewModal",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId && !fetched.current) {
      fetched.current = true;
      requestData();
    } else if (!productId && fetched.current) {
      // When the modal is closed this should update
      // to be prepared for the next request.
      setLoading(true);
      setData({});
      fetched.current = false;
    }
  }, [fetched, productId]);

  return [loading, { error, data }];
};

/**
 * This hook prepares the request function to add a product to the bag.
 * This was taken from the original QuickViewModal
 * @returns
 */
export const useAddToBag = () => {
  const graphqlClient = useClient();
  return async ({
    skus = [],
    sku,
    color,
    size,
    categoryTitle,
    selectedVariant,
    productSummary,
    productId,
  }) => {
    const bttKey = Math.floor(Math.random() * 10000);
    try {
      // Blue Triangle analytics
      // @TODO: Inquire about a more appropriate key (copy pasta from purchase-attributes.js)
      // ^ Above comments are from the original QVM.
      bttStart(bttKey, QUICK_VIEW_BTT_MODAL_NAME);
      const { id } = findSku({ skus, colorCode: color?.code, size }) || {};
      if (!id) {
        return {
          error: new Error(
            "Oops, an unknown error occurred. Please try again later."
          ),
        };
      }
      const { addToBag: bagResponse } = await shipAddToBag({
        client: graphqlClient,
        productId,
        sku: id,
        quantity: 1,
        checkForLowStock: false,
      });
      const cartId = bagResponse?.id;
      const cartTotal = bagResponse?.items?.shipItemCount;
      if (cartId) {
        if (cartTotal) {
          window.LLLNav.setBagItemCount(cartTotal);
        }
        // Set digital data global object for analytics
        const digitalDataCart = convertToDigitalDataCart({
          cartId,
          cartTotal,
          category: categoryTitle,
          color,
          colorVariant: selectedVariant,
          productID: productId,
          productName: productSummary?.title,
          sku,
          skuData: sku,
        });
        $window.digitalData.cart = {
          ...$window.digitalData.cart,
          ...digitalDataCart,
        };
        return {
          success: true,
        };
      } else {
        return {
          error: Error(
            "Oops, an unknown error occurred. Please try again later."
          ),
        };
      }
    } catch (e) {
      return {
        error: e,
      };
    } finally {
      bttEnd(bttKey, QUICK_VIEW_BTT_MODAL_NAME);
    }
  };
};
